import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Shield, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { setAuthState } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, formData);

      if (data.user.role !== 'admin') {
        toast.error('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }

      setAuthState(data.token, data.user);
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your admin email address');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: forgotEmail.trim(),
        userType: 'admin',
      });
      setForgotSent(true);
      toast.success('If the email exists, a password reset link has been sent.');
    } catch (error) {
      // The endpoint always responds success for privacy, but handle unexpected errors gracefully
      toast.error(error.response?.data?.detail || 'Unable to send reset link. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setMode('login');
    setForgotEmail('');
    setForgotSent(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>

      <Card className="w-full max-w-md p-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl relative z-10" data-testid="admin-login-card">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-primary to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {mode === 'login' ? 'Admin Portal' : 'Reset Admin Password'}
          </h1>
          <p className="text-gray-500 mt-1">प्रशासनिक पोर्टल</p>
          <p className="text-sm text-gray-400 mt-2">
            {mode === 'login'
              ? 'Mahasabha Administration'
              : 'Enter your registered admin email to receive a reset link.'}
          </p>
        </div>

        {mode === 'login' && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="adminEmail" className="text-gray-700 font-medium">
                <Mail className="w-4 h-4 inline-block mr-2 text-gray-400" />
                Email Address
              </Label>
              <Input
                id="adminEmail"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                placeholder="admin@example.com"
                className="h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                data-testid="admin-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminPassword" className="text-gray-700 font-medium">
                <Lock className="w-4 h-4 inline-block mr-2 text-gray-400" />
                Password
              </Label>
              <div className="relative">
                <Input
                  id="adminPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  placeholder="Enter your password"
                  className="h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors pr-12"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="text-sm font-medium text-primary hover:text-primary-600 hover:underline transition"
                data-testid="admin-forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Authenticating...
                </span>
              ) : (
                'Login to Admin Panel'
              )}
            </Button>
          </form>
        )}

        {mode === 'forgot' && !forgotSent && (
          <form onSubmit={handleForgotSubmit} className="space-y-5" data-testid="admin-forgot-form">
            <div className="space-y-2">
              <Label htmlFor="forgotEmail" className="text-gray-700 font-medium">
                <Mail className="w-4 h-4 inline-block mr-2 text-gray-400" />
                Admin Email Address
              </Label>
              <Input
                id="forgotEmail"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                placeholder="admin@example.com"
                className="h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                data-testid="admin-forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              disabled={forgotLoading}
              data-testid="admin-forgot-submit"
            >
              {forgotLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <button
              type="button"
              onClick={resetForgotState}
              className="w-full flex items-center justify-center text-sm text-gray-600 hover:text-gray-900 transition"
              data-testid="admin-forgot-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
            </button>
          </form>
        )}

        {mode === 'forgot' && forgotSent && (
          <div className="space-y-5 text-center" data-testid="admin-forgot-success">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              If an admin account exists for <strong>{forgotEmail}</strong>, a password reset link has been sent.
              Please check your inbox (and spam folder). The link is valid for 1 hour.
            </div>
            <Button
              type="button"
              onClick={resetForgotState}
              className="w-full h-12 bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="admin-forgot-return-login"
            >
              Return to Login
            </Button>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">Secure Admin Access Only</p>
          <p className="text-xs text-gray-400 mt-1">
            ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा)
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;
